import React, { createContext, useContext, useState, useCallback } from 'react';
import { CheckCircle2, AlertCircle, AlertTriangle, Info, X } from 'lucide-react';

const ToastContext = createContext(null);

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const addToast = useCallback((type, message, title = '', duration = 4000) => {
    const id = Date.now() + Math.random().toString(36).substr(2, 4);
    const newToast = { id, type, message, title, duration };

    setToasts((prev) => [...prev.slice(-4), newToast]); // Limit to max 5 simultaneous toasts

    if (duration > 0) {
      setTimeout(() => {
        removeToast(id);
      }, duration);
    }
    return id;
  }, [removeToast]);

  const toast = {
    success: (msg, title = 'Success', duration = 4000) => addToast('success', msg, title, duration),
    error: (msg, title = 'Error', duration = 5000) => addToast('error', msg, title, duration),
    warning: (msg, title = 'Warning', duration = 4500) => addToast('warning', msg, title, duration),
    info: (msg, title = 'Notice', duration = 4000) => addToast('info', msg, title, duration),
    remove: removeToast,
  };

  return (
    <ToastContext.Provider value={toast}>
      {children}
      {/* Toast Container Stack */}
      <div 
        aria-live="polite" 
        className="fixed top-4 right-4 z-[9999] flex flex-col gap-2 max-w-sm w-full pointer-events-none p-2 sm:p-0"
      >
        {toasts.map((t) => (
          <div
            key={t.id}
            role={t.type === 'error' ? 'alert' : 'status'}
            className={`pointer-events-auto flex items-start gap-3 p-3.5 rounded-xl shadow-xl border backdrop-blur-md transition-all duration-300 animate-in slide-in-from-top-2 fade-in ${
              t.type === 'success'
                ? 'bg-white/95 border-emerald-200 text-gray-800'
                : t.type === 'error'
                ? 'bg-white/95 border-red-200 text-gray-800'
                : t.type === 'warning'
                ? 'bg-white/95 border-amber-200 text-gray-800'
                : 'bg-white/95 border-teal-200 text-gray-800'
            }`}
          >
            {/* Icon */}
            <div className="shrink-0 mt-0.5">
              {t.type === 'success' && <CheckCircle2 className="w-5 h-5 text-emerald-500" />}
              {t.type === 'error' && <AlertCircle className="w-5 h-5 text-red-500" />}
              {t.type === 'warning' && <AlertTriangle className="w-5 h-5 text-amber-500" />}
              {t.type === 'info' && <Info className="w-5 h-5 text-[#00796b]" />}
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0 pr-1">
              {t.title && <div className="text-xs font-bold text-gray-900 leading-tight">{t.title}</div>}
              <div className="text-xs text-gray-600 mt-0.5 leading-relaxed break-words">{t.message}</div>
            </div>

            {/* Dismiss Button */}
            <button
              type="button"
              onClick={() => removeToast(t.id)}
              className="shrink-0 text-gray-400 hover:text-gray-600 p-0.5 rounded cursor-pointer transition-colors"
              aria-label="Close notification"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}
